import { Col, Text } from 'components';
import { imageResizer } from 'core';
import { theme } from 'lib';
import React, { useRef, useState } from 'react';
import { FiCamera, FiEdit2, FiEye, FiX } from 'react-icons/fi';
import styled from 'styled-components';

const Container = styled.div`
  position: relative;
  width: ${(props) => props.size || '120px'};
  height: ${(props) => props.size || '120px'};
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
`;

const ImagePreview = styled.div`
  width: 100%;
  height: 100%;
  background-image: ${(props) => `url(${props.src})`};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const Placeholder = styled.div`
  width: 100%;
  height: 100%;
  background: ${theme.GREY_10};
  border: 2px dashed ${theme.GREY_30};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.PRIMARY};
    background: ${theme.PRIMARY}08;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${Container}:hover & {
    opacity: 1;
  }
`;

const ActionButton = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: transform 0.15s ease;
  pointer-events: auto;

  &:hover {
    transform: scale(1.1);
  }
`;

// Fullscreen Preview Modal
const FullscreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  cursor: zoom-out;
`;

const FullscreenImage = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
`;

const CloseButton = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const SingleImageUploader = ({
  value,
  onChange,
  title = 'Image',
  size = '120px',
  showTitle = true
}) => {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);

  const handleEditClick = (e) => {
    e.stopPropagation();
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handlePreviewClick = (e) => {
    e.stopPropagation();
    setShowFullscreen(true);
  };

  const handleContainerClick = () => {
    // Only trigger file input if no image
    if (!hasImage && inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) {
      e.target.value = '';
    }

    if (!file) return;
    if (!file.type.startsWith('image/')) return;

    setLoading(true);

    try {
      const resized = await imageResizer({
        height: 800,
        width: 800,
        format: 'jpeg',
        file,
        output: 'blob',
        quality: 85
      });

      const preview = URL.createObjectURL(resized);

      onChange({
        file: resized,
        preview,
        url: null
      });
    } catch (error) {
      console.error('Failed to process image:', error);
    }

    setLoading(false);
  };

  const imageUrl = value?.preview || value?.url || value;
  const hasImage = !!imageUrl && typeof imageUrl === 'string';

  return (
    <>
      <Col noFlex>
        {showTitle && (
          <Text b2 med marg="0 0 8px">
            {title}
          </Text>
        )}

        <input
          type="file"
          ref={inputRef}
          accept="image/*"
          onChange={handleFileChange}
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0
          }}
        />

        <Container size={size} onClick={handleContainerClick}>
          {loading ? (
            <Placeholder>
              <Text c1 col={theme.GREY_50}>
                Loading...
              </Text>
            </Placeholder>
          ) : hasImage ? (
            <>
              <ImagePreview src={imageUrl} />
              <Overlay>
                <ActionButton onClick={handlePreviewClick} title="Preview">
                  <FiEye size={16} color={theme.GREY_80} />
                </ActionButton>
                <ActionButton onClick={handleEditClick} title="Change image">
                  <FiEdit2 size={16} color={theme.GREY_80} />
                </ActionButton>
              </Overlay>
            </>
          ) : (
            <Placeholder>
              <FiCamera size={24} color={theme.GREY_50} />
              <Text c1 col={theme.GREY_50}>
                Add Image
              </Text>
            </Placeholder>
          )}
        </Container>
      </Col>

      {/* Fullscreen Preview Modal */}
      {showFullscreen && hasImage && (
        <FullscreenOverlay onClick={() => setShowFullscreen(false)}>
          <CloseButton onClick={() => setShowFullscreen(false)}>
            <FiX size={24} color="white" />
          </CloseButton>
          <FullscreenImage src={imageUrl} alt="Preview" onClick={(e) => e.stopPropagation()} />
        </FullscreenOverlay>
      )}
    </>
  );
};

export default SingleImageUploader;
